/**
 * Master Drop delivery dates. <tk-md-dates> fills every [data-md-date] child with
 * today + N business days (data-min / data-max), skipping weekends and owner-listed holidays.
 * Computed client-side so full-page caching never shows yesterday's dates.
 */
const FORMATS = {
  month_day: { month: 'short', day: 'numeric' },
  weekday_month_day: { weekday: 'short', month: 'short', day: 'numeric' },
  day_month: { day: 'numeric', month: 'short' },
  weekday_day_month: { weekday: 'short', day: 'numeric', month: 'short' },
  numeric: { day: 'numeric', month: 'numeric' },
}

/** @param {Date} date */
const isoDay = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

class TkMdDates extends HTMLElement {
  connectedCallback() {
    this.render()
  }

  /**
   * @param {number} days
   * @param {boolean} skipWeekends
   * @param {Set<string>} holidays
   */
  addBusinessDays(days, skipWeekends, holidays) {
    const date = new Date()
    date.setHours(12, 0, 0, 0)
    let left = Math.max(0, Math.floor(days))
    const skip = (/** @type {Date} */ d) =>
      (skipWeekends && (d.getDay() === 0 || d.getDay() === 6)) || holidays.has(isoDay(d))
    while (left > 0) {
      date.setDate(date.getDate() + 1)
      if (!skip(date)) left -= 1
    }
    return date
  }

  render() {
    const skipWeekends = this.dataset.skipWeekends === 'true'
    const holidays = new Set(
      (this.dataset.skipDates ?? '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    )
    const options =
      FORMATS[/** @type {keyof typeof FORMATS} */ (this.dataset.format)] ?? FORMATS.month_day
    let formatter
    try {
      formatter = new Intl.DateTimeFormat(this.dataset.locale || undefined, options)
    } catch {
      formatter = new Intl.DateTimeFormat(undefined, options)
    }

    for (const el of this.querySelectorAll('[data-md-date]')) {
      if (!(el instanceof HTMLElement)) continue
      const min = Number(el.dataset.min) || 0
      const max = Math.max(min, Number(el.dataset.max) || 0)
      const start = formatter.format(this.addBusinessDays(min, skipWeekends, holidays))
      const end = formatter.format(this.addBusinessDays(max, skipWeekends, holidays))
      el.textContent = start === end ? start : `${start} – ${end}`
    }
    this.classList.add('is-ready')
  }
}

if (!customElements.get('tk-md-dates')) customElements.define('tk-md-dates', TkMdDates)
